// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.101Z
// Collection: creditTopups/$topupId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Onchain credit purchase (top-up). isPassthrough: pays TOPUP_LAMPORTS (0.1 test SOL) from buyer to the treasury (PROJECT_VAULT_ADDRESS) and, chained with && so credits are granted ONLY on payment success, grants TOPUP_CREDITS (15) to the buyer's userCredits balance (a missing record initializes to 50+15=65). Matches the rental treasury pattern (rentalDeposits pays to PROJECT_VAULT_ADDRESS).
 */

export interface CreditTopupsRequest {
  buyer: AddressType;
  lamportsPaid: number | TimeOperation | IncrementOperation | TokenAmount;
  creditsAdded: number | TimeOperation | IncrementOperation | TokenAmount;
  timestamp: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface CreditTopupsResponse {
  buyer: string;
  lamportsPaid: number;
  creditsAdded: number;
  timestamp: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a CreditTopups operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildCreditTopups(topupId: string, data: CreditTopupsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `creditTopups/${topupId}`, document: doc };
}

/**
 * Logged-in buyer only, and @newData.buyer MUST equal @user.address. lamportsPaid MUST equal TOPUP_LAMPORTS (100000000 lamports = 0.1 test SOL) and creditsAdded MUST equal TOPUP_CREDITS (15) — no other amounts allowed. The onchain create hook FIRST transfers TOPUP_LAMPORTS of native SOL from the buyer (@user.address, who signs) to the treasury (PROJECT_VAULT_ADDRESS) via @TokenPlugin.transfer, THEN (chained with &&, so the grant is gated on a successful transfer) increments the buyer's userCredits.balance by TOPUP_CREDITS — initializing a missing record to NEW_WALLET_CREDITS+TOPUP_CREDITS (50+15=65). A user CANNOT gain credits without a successful payment. timestamp is unix seconds. isPassthrough: runs onchain, record stays queryable offchain. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setCreditTopups(topupId: string, data: CreditTopupsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`creditTopups/${topupId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting CreditTopups: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Anyone can read top-up records (buyer, lamportsPaid, creditsAdded, timestamp). Required for onchain collections.
   (Get Single Item)
 */
export async function getCreditTopups(topupId: string): Promise<CreditTopupsResponse | null> {
  try {
    const result = await get(`creditTopups/${topupId}`);
    return result as CreditTopupsResponse | null;
  } catch (error) {
    console.error(`Error getting CreditTopups: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single CreditTopups document. (
  Read Operation Details: Public. Anyone can read top-up records (buyer, lamportsPaid, creditsAdded, timestamp). Required for onchain collections.
  )
 */
export function subscribeCreditTopups(callback: (data: CreditTopupsResponse | null) => void, topupId: string): Promise<() => Promise<void>> {
  return subscribe(`creditTopups/${topupId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as CreditTopupsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to CreditTopups at path '${`creditTopups/${topupId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many CreditTopups items from collection creditTopups
 
  Read Operation Details: Public. Anyone can read top-up records (buyer, lamportsPaid, creditsAdded, timestamp). Required for onchain collections.
  
 */
export async function getManyCreditTopups(filter: string = ""): Promise<CreditTopupsResponse[]> {
  try {
    const results = await get("creditTopups", { prompt: filter });
    return Array.isArray(results) ? results as CreditTopupsResponse[] : [];
  } catch (error) {
    console.error(`Error getting CreditTopups collection at path 'creditTopups': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in CreditTopups collection at creditTopups
 
  Read Operation Details: Public. Anyone can read top-up records (buyer, lamportsPaid, creditsAdded, timestamp). Required for onchain collections.
  
 */
export function subscribeManyCreditTopups(callback: (data: CreditTopupsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("creditTopups", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as CreditTopupsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to CreditTopups collection at path '${"creditTopups"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all CreditTopups items from collection creditTopups
 
  Read Operation Details: Public. Anyone can read top-up records (buyer, lamportsPaid, creditsAdded, timestamp). Required for onchain collections.
  
 */
export async function getAllCreditTopups(filter: string = ""): Promise<CreditTopupsResponse[]> {
  try {
    const results = await get("creditTopups", { prompt: filter });
    return Array.isArray(results) ? results as CreditTopupsResponse[] : [];
  } catch (error) {
    console.error(`Error getting CreditTopups collection at path 'creditTopups': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in CreditTopups collection at creditTopups
 
  Read Operation Details: Public. Anyone can read top-up records (buyer, lamportsPaid, creditsAdded, timestamp). Required for onchain collections.
  
 */
export function subscribeAllCreditTopups(callback: (data: CreditTopupsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("creditTopups", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as CreditTopupsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to CreditTopups collection at path '${"creditTopups"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count CreditTopups items in collection creditTopups.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countCreditTopups(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("creditTopups", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting CreditTopups at path 'creditTopups': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on CreditTopups items in collection creditTopups.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateCreditTopups(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("creditTopups", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating CreditTopups at path 'creditTopups': ${formatError(error)}`);
    return { value: 0 };
  }
}

